'use client';

/**
 * VideoBackground — lazy, IntersectionObserver-paused video background.
 * Falls back to poster on save-data or 2G/3G mobile connections.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-012
 *
 * Note: prefers-reduced-motion is intentionally NOT honored here. The W3C
 * media-queries spec scopes that preference to "essential" animations (parallax,
 * scroll-jacking, flashing). A muted, slow-loop, dark-overlay ambient background
 * is decorative and out of that scope; honoring it would silently break the
 * hero on every macOS user with "Reduce motion" enabled (default for many).
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface VideoBackgroundProps {
  srcWebm: string;
  srcMp4: string;
  poster: string;
  loop?: boolean;
  className?: string;
  /** Tint overlay opacity (0–1). Default 0.3 (matches dark glass cards). */
  overlayOpacity?: number;
}

type ConnectionLike = { saveData?: boolean; effectiveType?: string };

// Conservative UA list — covers the crawlers that actually paint pages
// (Googlebot's headless renderer was making the video element cover the
// hero text in Search Console "Test live URL" mode).
const CRAWLER_UA_REGEX =
  /(googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|facebot|facebookexternalhit|twitterbot|applebot|linkedinbot|gptbot|claudebot|perplexitybot|chatgpt-user|google-extended)/i;

function isCrawlerUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  return CRAWLER_UA_REGEX.test(navigator.userAgent || '');
}

function shouldServePosterOnly(): boolean {
  if (typeof window === 'undefined') return true;
  const conn = (navigator as Navigator & { connection?: ConnectionLike }).connection;
  if (conn?.saveData) return true;
  if (
    window.innerWidth < 768 &&
    conn?.effectiveType &&
    ['slow-2g', '2g', '3g'].includes(conn.effectiveType)
  ) {
    return true;
  }
  return false;
}

export function VideoBackground({
  srcWebm,
  srcMp4,
  poster,
  loop = true,
  className,
  overlayOpacity = 0.3,
}: VideoBackgroundProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [posterOnly, setPosterOnly] = useState(true);
  // Crawler check runs once on mount. SSR renders the poster (default), then
  // if the visitor is a crawler we unmount entirely (return null) so bots get
  // a lighter page and never hit the stacking-context bug where Google's
  // headless renderer paints the <video> element on top of the hero text.
  const [isCrawler, setIsCrawler] = useState(false);

  useEffect(() => {
    if (isCrawlerUA()) {
      setIsCrawler(true);
      return;
    }
    setPosterOnly(shouldServePosterOnly());
  }, []);

  useEffect(() => {
    if (isCrawler || posterOnly) return;
    const el = videoRef.current;
    if (!el) return;

    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[VideoBackground] autoplay blocked:', err?.message ?? err);
          }
        });
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) tryPlay();
        else el.pause();
      },
      { rootMargin: '200px' }
    );

    observer.observe(el);
    el.addEventListener('loadeddata', tryPlay);
    return () => {
      observer.disconnect();
      el.removeEventListener('loadeddata', tryPlay);
    };
  }, [posterOnly, isCrawler]);

  if (isCrawler) return null;

  return (
    <div className={cn('absolute inset-0 z-0 overflow-hidden', className)} aria-hidden="true">
      {posterOnly ? (
        <img
          src={poster}
          alt=""
          className="h-full w-full object-cover"
          // Hero is above the fold — eager so Googlebot/social previews
          // capture the visual instead of an empty black div.
          loading="eager"
          fetchPriority="high"
        />
      ) : (
        <video
          ref={videoRef}
          poster={poster}
          loop={loop}
          muted
          autoPlay
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
        >
          <source src={srcWebm} type="video/webm" />
          <source src={srcMp4} type="video/mp4" />
        </video>
      )}
      <div className="absolute inset-0 bg-ink-950" style={{ opacity: overlayOpacity }} />
    </div>
  );
}
